import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Menu,
    Card,
    CardContent,
    CardHeader,
    InputAdornment,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Tooltip
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    X,
    MeetingRoom,
    PersonAdd,
    CalendarToday,
    FlightLand,
    Hotel,
    Payment,
    AttachMoney,
    Comment,
    Person,
    Phone,
    Email,
    LocationOn,
    CreditCard,
    CheckCircle,
    Group,
    ChildCare,
    LocalOffer,
    Smartphone,
    Link,
    Numbers,
    Business,
    People,
    AddCircleOutline,
    RemoveCircleOutline,
    Print
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    APIRoomNames,
    APIBookingList,
    APIBookingStore,
    APIBookingUpdate,
    APIBookingDelete,
    APIBookingTypeNames,
    APIBookingNames
} from '../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import {
    formatCreatedAtDate,
    formatNumberWithComma,
    getCategoriesNameById,
    getStatusById,
    getUserDetails,
    recordsPerPage,
    formatCurrency,
    formatNumberWithComma2
} from '../../utils/helpers';
import {
    exportToCSV,
    exportToExcel,
    exportToPDFEnhanced
} from '../../services/reservation/BookingService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import useCustomToast from '../../hooks/useToast';
import { banksData, paymentModes } from '../../utils/dataInfo';
import PrintBooking from './PrintBooking';

const Booking = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: []
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [rooms, setRooms] = useState([]);

    // Print receipt state
    const [printBookingData, setPrintBookingData] = useState(null);
    const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);

    // Organisation members state
    const [organisationMembers, setOrganisationMembers] = useState([
        { name: '', email: '', phone: '', room_type: '' }
    ]);

    // Main form state
    const [formData, setFormData] = useState({
        // Reservation Details
        booking_no: '',
        check_in: '',
        check_out: '',
        arrival_from: '',
        booking_type: '',
        purpose_of_visit: '',
        booking_remarks: '',
        
        // Room Details
        room_type: '',
        adults: 1,
        children: 0,
        
        // Customer Details - Individual
        customer_name: '',
        customer_mobile: '',
        customer_email: '',
        
        // Organisation Details
        organisation_name: '',
        
        // Payment Details
        discount_reason: '',
        discount_amount: 0,
        commission_rate: 0,
        commission_amount: 0,
        
        // Billing Details
        booking_charge: 0,
        tax_charge: 0,
        service_charge: 0,
        total_charge: 0,
        
        // Advance Details
        payment_mode: '',
        bank_name: '',
        card_no: '',
        mobile_money_phone: '',
        mobile_money_ref: '',
        advance_remarks: '',
        advance_amount: 0,
        total_amount: 0,
        
        // Status
        status: 1
    });

    const [filters, setFilters] = useState({
        booking_no: '',
        customer_name: '',
        room_type: '',
        status: '',
        dateFrom: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        check_in: false,
        check_out: false,
        room_type: false,
        customer_name: false,
        customer_mobile: false,
        organisation_name: false,
        organisation_members: false
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);

    // Stepper state
    const [activeStep, setActiveStep] = useState(0);
    const steps = ['Reservation Details', 'Room & Customer', 'Payment Details', 'Confirmation'];

    // Permissions
    const [userRole, setUserRole] = useState('');
    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
    }, []);

    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    useEffect(() => {
        fetchData();
        fetchRooms();
    }, []);

    // Add effect to reset member fields when booking type changes
    useEffect(() => {
        if (formData.booking_type === 1) { // Individual
            // Clear organisation fields
            setFormData(prev => ({
                ...prev,
                organisation_name: ''
            }));
        } else if (formData.booking_type === 2) { // Organisation
            // Clear individual fields
            setFormData(prev => ({
                ...prev,
                customer_name: '',
                customer_mobile: '',
                customer_email: ''
            }));
        }
    }, [formData.booking_type]);

    const fetchRooms = async () => {
        try {
            const response = await axios.get(APIRoomNames);
            if (response.data.status === 'success') {
                setRooms(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching room names:", error);
            showToast("Error", "Failed to load rooms", "error");
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [bookingRes] = await Promise.all([
                axios.get(APIBookingList)
            ]);

            const bookings = Array.isArray(bookingRes.data.data) ? bookingRes.data.data.reverse() : [];

            setPageState(prev => ({
                ...prev,
                pageData: bookings
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    // Handle print booking
    const handlePrintBooking = (booking) => {
        setPrintBookingData(booking);
        setIsPrintDialogOpen(true);
    };

    const handleClosePrintDialog = () => {
        setIsPrintDialogOpen(false);
        setPrintBookingData(null);
    };

    // Organisation members handlers
    const handleAddMember = () => {
        setOrganisationMembers([
            ...organisationMembers,
            { name: '', email: '', phone: '', room_type: '' }
        ]);
    };

    const handleRemoveMember = (index) => {
        const updatedMembers = organisationMembers.filter((_, i) => i !== index);
        setOrganisationMembers(updatedMembers);
    };

    const handleMemberChange = (index, field, value) => {
        const updatedMembers = [...organisationMembers];
        updatedMembers[index][field] = value;
        setOrganisationMembers(updatedMembers);
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(record => {
        const customerDisplay = record.booking_type === 2 
            ? record.organisation_name || ''
            : record.customer_name || '';
            
        const matchesSearch = searchTerm === '' ||
            (record.booking_no && record.booking_no.toString().includes(searchTerm)) ||
            (customerDisplay.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesBookingNo = !filters.booking_no ||
            (record.booking_no && record.booking_no.toString().includes(filters.booking_no));

        const matchesCustomer = !filters.customer_name ||
            (customerDisplay.toLowerCase().includes(filters.customer_name.toLowerCase()));

        const matchesRoom = !filters.room_type ||
            (record.room_type && record.room_type.toString() === filters.room_type.toString());

        const matchesStatus = !filters.status ||
            (record.status && record.status.toString().includes(filters.status));

        // Date range filter
        let matchesDate = true;
        if (filters.dateFrom && record.check_in) {
            const recordDate = new Date(record.check_in).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate >= filterFromDate;
        }

        if (filters.dateTo && record.check_out) {
            const recordDate = new Date(record.check_out).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate <= filterToDate;
        }

        return matchesSearch && matchesBookingNo && matchesCustomer && matchesRoom && matchesStatus && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleOpenAdd = () => {
        setPostStatus("ADD");
        resetForm();
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
        setActiveStep(0);
        resetForm();
        setOrganisationMembers([{ name: '', email: '', phone: '', room_type: '' }]);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenEdit = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");
        
        // Set form data from record
        setFormData({
            booking_no: record.booking_no || '',
            check_in: record.check_in ? record.check_in.split('T')[0] : '',
            check_out: record.check_out ? record.check_out.split('T')[0] : '',
            arrival_from: record.arrival_from || '',
            booking_type: record.booking_type || '',
            purpose_of_visit: record.purpose_of_visit || '',
            booking_remarks: record.booking_remarks || '',
            room_type: record.room_type,
            adults: record.adults || 1,
            children: record.children || 0,
            customer_name: record.customer_name || '',
            customer_mobile: record.customer_mobile || '',
            customer_email: record.customer_email || '',
            organisation_name: record.organisation_name || '',
            discount_reason: record.discount_reason || '',
            discount_amount: record.discount_amount || 0,
            commission_rate: record.commission_rate || 0,
            commission_amount: record.commission_amount || 0,
            booking_charge: record.booking_charge || 0,
            tax_charge: record.tax_charge || 0,
            service_charge: record.service_charge || 0,
            total_charge: record.total_charge || 0,
            payment_mode: record.payment_mode || '',
            bank_name: record.bank_name || '',
            card_no: record.card_no || '',
            mobile_money_phone: record.mobile_money_phone || '',
            mobile_money_ref: record.mobile_money_ref || '',
            advance_remarks: record.advance_remarks || '',
            advance_amount: record.advance_amount || 0,
            total_amount: record.total_amount || 0,
            status: record.status || 1
        });
        
        // Set organisation members if exists
        if (record.organisation_members && record.organisation_members.length > 0) {
            setOrganisationMembers(record.organisation_members);
        }
        
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Don't allow editing booking_no
        if (name === 'booking_no') return;

        // Reset error for this field
        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Calculate charges if booking charge changes
        if (name === 'booking_charge' || name === 'discount_amount') {
            calculateCharges();
        }
    };

    const calculateCharges = () => {
        const bookingCharge = parseFloat(formData.booking_charge) || 0;
        const discountAmount = parseFloat(formData.discount_amount) || 0;
        const totalCharge = bookingCharge - discountAmount;

        setFormData(prev => ({
            ...prev,
            total_charge: totalCharge,
            total_amount: totalCharge
        }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        setFilterOptions(false);
    };

    const clearFilters = () => {
        setFilters({
            booking_no: '',
            customer_name: '',
            room_type: '',
            status: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        setFilterOptions(false);
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handleExportExcel = async () => {
        try {
            handleExportClose();
            await exportToExcel(filteredData, rooms, {
                booking_no: filters.booking_no,
                customer_name: filters.customer_name,
                room_type: filters.room_type,
                status: filters.status,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo
            });
            showToast("Success", "Excel file downloaded successfully", "success");
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportPDF = async () => {
        try {
            handleExportClose();
            await exportToPDFEnhanced(filteredData, rooms, [], {
                booking_no: filters.booking_no,
                customer_name: filters.customer_name,
                room_type: filters.room_type,
                status: filters.status,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo
            });
            showToast("Success", "PDF file downloaded successfully", "success");
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportCSV = async () => {
        try {
            handleExportClose();
            await exportToCSV(filteredData, rooms);
            showToast("Success", "CSV file downloaded successfully", "success");
        } catch (error) {
            console.error('CSV export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    // Stepper handlers
    const handleNext = () => {
        // Validate current step
        if (activeStep === 0) {
            if (!formData.check_in || !formData.check_out || !formData.booking_type) {
                showToast("Validation Error", "Please fill all required fields (Check-in, Check-out, Booking Type)", "warning");
                return;
            }
            // Check if dates are valid
            if (formData.check_in && formData.check_out) {
                const checkInDate = new Date(formData.check_in);
                const checkOutDate = new Date(formData.check_out);
                if (checkOutDate <= checkInDate) {
                    showToast("Validation Error", "Check-out date must be after check-in date", "warning");
                    return;
                }
            }
        } else if (activeStep === 1) {
            // Validate based on booking type
            if (formData.booking_type === 1) { // Individual
                if (!formData.room_type) {
                    showToast("Validation Error", "Please select room type", "warning");
                    return;
                }
                if (!formData.customer_name) {
                    showToast("Validation Error", "Please enter customer name", "warning");
                    return;
                }
                if (!formData.customer_mobile) {
                    showToast("Validation Error", "Please enter customer mobile number", "warning");
                    return;
                }
            } else if (formData.booking_type === 2) { // Organisation
                if (!formData.organisation_name) {
                    showToast("Validation Error", "Please enter organisation name", "warning");
                    return;
                }
                
                // Validate at least one member
                if (organisationMembers.length === 0 || 
                    !organisationMembers[0].name || 
                    !organisationMembers[0].phone || 
                    !organisationMembers[0].room_type) {
                    showToast("Validation Error", "Please add at least one member with all details", "warning");
                    return;
                }
                
                // Validate all members
                for (let i = 0; i < organisationMembers.length; i++) {
                    const member = organisationMembers[i];
                    if (!member.name || !member.phone || !member.room_type) {
                        showToast("Validation Error", `Member ${i + 1} is missing required fields`, "warning");
                        return;
                    }
                }
            }
        } else if (activeStep === 2) {
            if (!formData.payment_mode) {
                showToast("Validation Error", "Please select payment method", "warning");
                return;
            }
            // Validate mobile money details if payment mode is mobile
            if (formData.payment_mode === 'mobile') {
                if (!formData.mobile_money_phone || !formData.mobile_money_ref) {
                    showToast("Validation Error", "Please fill mobile money phone and reference number", "warning");
                    return;
                }
            }
            // Validate bank details if payment mode is bank
            if (formData.payment_mode === 'bank') {
                if (!formData.bank_name || !formData.card_no) {
                    showToast("Validation Error", "Please select bank name and enter card/account number", "warning");
                    return;
                }
            }
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const resetForm = () => {
        setFormData({
            booking_no: '',
            check_in: '',
            check_out: '',
            arrival_from: '',
            booking_type: '',
            purpose_of_visit: '',
            booking_remarks: '',
            room_type: '',
            adults: 1,
            children: 0,
            customer_name: '',
            customer_mobile: '',
            customer_email: '',
            organisation_name: '',
            discount_reason: '',
            discount_amount: 0,
            commission_rate: 0,
            commission_amount: 0,
            booking_charge: 0,
            tax_charge: 0,
            service_charge: 0,
            total_charge: 0,
            payment_mode: '',
            bank_name: '',
            card_no: '',
            mobile_money_phone: '',
            mobile_money_ref: '',
            advance_remarks: '',
            advance_amount: 0,
            total_amount: 0,
            status: 1
        });
        setOrganisationMembers([{ name: '', email: '', phone: '', room_type: '' }]);
    };

    const validateForm = () => {
        const errors = [];
        if (!formData.check_in) errors.push('Check-in date is required');
        if (!formData.check_out) errors.push('Check-out date is required');
        if (!formData.booking_type) errors.push('Booking type is required');
        
        // Validate based on booking type
        if (formData.booking_type === 1) { // Individual
            if (!formData.customer_name) errors.push('Customer name is required');
            if (!formData.room_type) errors.push('Room type is required');
            if (!formData.customer_mobile) errors.push('Customer mobile is required');
        } else if (formData.booking_type === 2) { // Organisation
            if (!formData.organisation_name) errors.push('Organisation name is required');
            
            // Validate members
            if (organisationMembers.length === 0) {
                errors.push('At least one member is required');
            } else {
                organisationMembers.forEach((member, index) => {
                    if (!member.name) errors.push(`Member ${index + 1}: Name is required`);
                    if (!member.phone) errors.push(`Member ${index + 1}: Phone is required`);
                    if (!member.room_type) errors.push(`Member ${index + 1}: Room type is required`);
                });
            }
        }
        
        if (!formData.payment_mode) errors.push('Payment mode is required');

        // Validate payment details
        if (formData.payment_mode === 'mobile') {
            if (!formData.mobile_money_phone) errors.push('Mobile money phone is required');
            if (!formData.mobile_money_ref) errors.push('Mobile money reference is required');
        }
        if (formData.payment_mode === 'bank') {
            if (!formData.bank_name) errors.push('Bank name is required');
            if (!formData.card_no) errors.push('Card/Account number is required');
        }

        if (errors.length > 0) {
            showToast("Validation Error", errors.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const handleDialogClose = () => {
        setOpenAdd(false);
        setOpenView(false);
        setOpenDelete(false);
        setFilterOptions(false);
        setActiveStep(0);
        resetForm();
        setOrganisationMembers([{ name: '', email: '', phone: '', room_type: '' }]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        
        // Append all form data
        Object.keys(formData).forEach(key => {
            if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
                formDataToSend.append(key, formData[key]);
            }
        });

        // Append organisation members individually
        if (formData.booking_type === "2" && organisationMembers.length > 0) {
            organisationMembers.forEach((member, index) => {
                formDataToSend.append(`organisation_members[${index}][name]`, member.name);
                formDataToSend.append(`organisation_members[${index}][email]`, member.email || '');
                formDataToSend.append(`organisation_members[${index}][phone]`, member.phone);
                formDataToSend.append(`organisation_members[${index}][room_type]`, member.room_type);
            });
        }

        // Only append updateId for UPDATE/DELETE operations
        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let apiUrl, method;
            if (postStatus === "ADD") {
                apiUrl = APIBookingStore;
                method = 'post';
            } else if (postStatus === "UPDATE") {
                apiUrl = APIBookingUpdate;
                method = 'post';
            } else if (postStatus === "DELETE") {
                apiUrl = APIBookingDelete;
                method = 'post';
            }

            const response = await axios({
                method,
                url: apiUrl,
                data: formDataToSend
            });

            if (response.data.status === "success") {
                showToast("Success", response.data.message, "success");
                handleDialogClose();
                fetchData();
            } else {
                showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
            }
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                if (status === 422) {
                    const errors = data.errors || {};
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(`${field}: ${errors[field].join(', ')}`);
                    }
                    showToast(
                        "Validation Error",
                        errorMessages.join('\n') || "Please correct the form errors",
                        "error"
                    );
                } else if (status === 409) {
                    showToast("Error", data.message || "This record already exists", "error");
                } else if (status >= 500) {
                    showToast("Server Error", data.message || "Internal server error", "error");
                } else {
                    showToast("Error", data.message || "Request failed", "error");
                }
            } else if (error.request) {
                showToast("Network Error", "No response from server. Please check your connection.", "error");
            } else {
                showToast("Error", error.message || "An unexpected error occurred", "error");
            }
            console.error("API Error:", error);
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                mb: { xs: 1, sm: 0 }
            }}>
                Booking Management ({formatNumberWithComma2(pageState.pageData.length || 0)})
            </Typography>

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    flexWrap: 'wrap' 
                }}>
                    {/* Buttons - column on mobile, row on desktop */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexShrink: 0,
                        order: { xs: 2, sm: 1 }
                    }}>
                        {hasPermission(PERMISSIONS.ADD) && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                                size="small"
                            >
                                New Booking
                            </Button>
                        )}
                        
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={() => setFilterOptions(true)}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                            size="small"
                        >
                            View Options
                        </Button>
                        
                        {hasPermission(PERMISSIONS.EXPORT) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )}
                        
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={fetchData}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>

                    {/* Search Bar - full width on mobile, fixed on desktop */}
                    <Paper sx={{
                        width: { xs: '100%', sm: '300px' },
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: { xs: 0, sm: 'auto' },
                        order: { xs: 1, sm: 2 },
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search Bookings..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters as a separate row below */}
                {(filters.booking_no || filters.dateFrom || filters.dateTo || filters.status || filters.room_type || filters.customer_name) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.booking_no && (
                            <Chip
                                label={`Booking #: ${filters.booking_no}`}
                                onDelete={() => setFilters(prev => ({ ...prev, booking_no: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.customer_name && (
                            <Chip
                                label={`Customer: ${filters.customer_name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, customer_name: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.room_type && (
                            <Chip
                                label={`Room: ${getCategoriesNameById(filters.room_type, rooms)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, room_type: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.status && (
                            <Chip
                                label={`Status: ${getStatusById(filters.status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            sx={{ minWidth: 'auto' }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Booking #</TableCell>
                                    <TableCell>Customer/Organisation</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Check In</TableCell>
                                    <TableCell>Check Out</TableCell>
                                    <TableCell>Room Type</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((booking, index) => (
                                    <TableRow
                                        key={booking.id}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={page * rowsPerPage + index + 1}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            {booking.booking_no || `BK${booking.id.toString().padStart(5, '0')}`}
                                        </TableCell>
                                        <TableCell>
                                            {booking.booking_type === 2 ? (
                                                <>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {booking.organisation_name || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        <People fontSize="small" sx={{ fontSize: 12, mr: 0.5 }} />
                                                        {booking.organisation_members?.length || 0} members
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="body2">
                                                        {booking.customer_name || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {booking.customer_mobile || ''}
                                                    </Typography>
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.booking_type === 1 ? 'Individual' : 'Organisation'}
                                                size="small"
                                                color={booking.booking_type === 1 ? 'default' : 'secondary'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{booking.check_in ? new Date(booking.check_in).toLocaleDateString() : 'N/A'}</TableCell>
                                        <TableCell>{booking.check_out ? new Date(booking.check_out).toLocaleDateString() : 'N/A'}</TableCell>
                                        <TableCell>
                                            {getCategoriesNameById(booking.room_type, rooms)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={formatCurrency(booking.total_amount || 0)}
                                                color="success"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {booking.status === 1 ? (
                                                <Chip
                                                    label={getStatusById(booking.status)}
                                                    size="small"
                                                    color="primary"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            ) : booking.status === 2 ? (
                                                <Chip
                                                    label="Checked In"
                                                    size="small"
                                                    color="success"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            ) : booking.status === 3 ? (
                                                <Chip
                                                    label="Checked Out"
                                                    size="small"
                                                    color="info"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            ) : (
                                                <Chip
                                                    label={getStatusById(booking.status)}
                                                    size="small"
                                                    color="error"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                edge="end"
                                                onClick={() => handleOpenView(booking)}
                                                color="primary"
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.info.light
                                                    }
                                                }}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                onClick={() => handlePrintBooking(booking)}
                                                color="secondary"
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.secondary.light
                                                    }
                                                }}
                                                title="Print Booking"
                                            >
                                                <Print />
                                            </IconButton>
                                            {hasPermission(PERMISSIONS.EDIT) && (
                                                <>
                                                    {/* Checked out disable Editing */}
                                                    {booking.status === 3 ? (<></>
                                                    ) : (
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleOpenEdit(booking)}
                                                            color="warning"
                                                            sx={{
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.success.light
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    )}
                                                </>)}
                                            {hasPermission(PERMISSIONS.DELETE) && (<>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpenDelete(booking)}
                                                    color="error"
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.error.light
                                                        }
                                                    }}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Pagination */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Search sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No bookings found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
                <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="info" />
                    Export to CSV (.csv)
                </MenuItem>
            </Menu>

            {/* Options Dialog */}
            <Dialog
                open={filterOptions}
                onClose={() => setFilterOptions(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Bookings
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Bookings
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Booking Number
                        </Typography>

                        <TextField
                            fullWidth
                            label="Booking Number"
                            name="booking_no"
                            value={filters.booking_no}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Customer/Organisation Name
                        </Typography>

                        <TextField
                            fullWidth
                            label="Customer/Organisation Name"
                            name="customer_name"
                            value={filters.customer_name}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Room Type
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Room Type</InputLabel>
                            <Select
                                name="room_type"
                                value={filters.room_type}
                                onChange={handleFilterChange}
                                label="Room Type"
                            >
                                <MenuItem value="">All Rooms</MenuItem>
                                {rooms.map((room) => (
                                    <MenuItem key={room.id} value={room.id}>
                                        {room.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="1">Active</MenuItem>
                                <MenuItem value="2">Checked In</MenuItem>
                                <MenuItem value="3">Checked Out</MenuItem>
                                <MenuItem value="0">Cancelled</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => setFilterOptions(false)}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => setFilterOptions(false)}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openAdd}
                onClose={handleCloseAdd}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="lg"
                scroll="paper"
                sx={{
                    '& .MuiDialog-paper': {
                        maxHeight: '90vh',
                        height: 'auto',
                    }
                }}
            >
                <DialogTitle sx={{
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: 1
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'semibold', flex: 1 }}>
                        {postStatus === "ADD" ? "Create New Booking" : "Edit Booking"}
                    </Typography>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={handleCloseAdd}
                        aria-label="close"
                        size="small"
                    >
                        <X />
                    </IconButton>
                </DialogTitle>
                
                {/* Stepper */}
                <Box sx={{ px: 3, pt: 2 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <DialogContent dividers>
                    <Box component="form" onSubmit={handleSubmit}>
                        {activeStep === 0 && (
                            <Card sx={{ mb: 3 }}>
                                <CardHeader
                                    title="Reservation Details"
                                    avatar={<CalendarToday color="primary" />}
                                />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Check In *"
                                                type="date"
                                                name="check_in"
                                                value={formData.check_in}
                                                onChange={handleInputChange}
                                                InputLabelProps={{ shrink: true }}
                                                required
                                                error={errors.check_in}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Check Out *"
                                                type="date"
                                                name="check_out"
                                                value={formData.check_out}
                                                onChange={handleInputChange}
                                                InputLabelProps={{ shrink: true }}
                                                required
                                                error={errors.check_out}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Arrival From"
                                                name="arrival_from"
                                                value={formData.arrival_from}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Airport, Station"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <FlightLand />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth required>
                                                <InputLabel>Booking Type *</InputLabel>
                                                <Select
                                                    name="booking_type"
                                                    value={formData.booking_type}
                                                    onChange={handleInputChange}
                                                    label="Booking Type *"
                                                >
                                                    <MenuItem value="">
                                                        <em>Select Booking Type</em>
                                                    </MenuItem>
                                                    <MenuItem value="1">
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Person fontSize="small" />
                                                            Individual
                                                        </Box>
                                                    </MenuItem>
                                                    <MenuItem value="2">
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Business fontSize="small" />
                                                            Organisation
                                                        </Box>
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Purpose of Visit"
                                                name="purpose_of_visit"
                                                value={formData.purpose_of_visit}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Business, Vacation"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Remarks"
                                                name="booking_remarks"
                                                value={formData.booking_remarks}
                                                onChange={handleInputChange}
                                                multiline
                                                rows={2}
                                                placeholder="Any special requirements or notes"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}

                        {activeStep === 1 && (
                            <>
                                <Card sx={{ mb: 3 }}>
                                    <CardHeader 
                                        title="Room Details" 
                                        avatar={<MeetingRoom color="primary" />}
                                    />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth required error={errors.room_type}>
                                                    <InputLabel>Room Type *</InputLabel>
                                                    <Select
                                                        name="room_type"
                                                        value={formData.room_type}
                                                        onChange={handleInputChange}
                                                        label="Room Type *"
                                                    >
                                                        <MenuItem value="">
                                                            <em>Select Room Type</em>
                                                        </MenuItem>
                                                        {rooms.map((room) => (
                                                            <MenuItem key={room.id} value={room.id}>
                                                                {room.roomtype}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    fullWidth
                                                    label="Adults"
                                                    type="number"
                                                    name="adults"
                                                    value={formData.adults}
                                                    onChange={handleInputChange}
                                                    inputProps={{ min: 1 }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Group />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    fullWidth
                                                    label="Children"
                                                    type="number"
                                                    name="children"
                                                    value={formData.children}
                                                    onChange={handleInputChange}
                                                    inputProps={{ min: 0 }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <ChildCare />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>

                                {formData.booking_type === "1" ? (
                                    // Individual Booking Form
                                    <Card sx={{ mb: 3 }}>
                                        <CardHeader 
                                            title="Individual Customer Details" 
                                            avatar={<Person color="primary" />}
                                        />
                                        <CardContent>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Customer Name *"
                                                        name="customer_name"
                                                        value={formData.customer_name}
                                                        onChange={handleInputChange}
                                                        required
                                                        error={errors.customer_name}
                                                        helperText={errors.customer_name ? "Customer name is required" : ""}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Person />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                                
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Mobile No *"
                                                        name="customer_mobile"
                                                        value={formData.customer_mobile}
                                                        onChange={handleInputChange}
                                                        required
                                                        error={errors.customer_mobile}
                                                        helperText={errors.customer_mobile ? "Mobile number is required" : "10 digits"}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Phone />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                                
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Email"
                                                        name="customer_email"
                                                        type="email"
                                                        value={formData.customer_email}
                                                        onChange={handleInputChange}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Email />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ) : formData.booking_type === "2" ? (
                                    // Organisation Booking Form
                                    <Card sx={{ mb: 3 }}>
                                        <CardHeader 
                                            title="Organisation Details" 
                                            avatar={<Business color="primary" />}
                                            action={
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<AddCircleOutline />}
                                                    onClick={handleAddMember}
                                                >
                                                    Add Member
                                                </Button>
                                            }
                                        />
                                        <CardContent>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Organisation Name *"
                                                        name="organisation_name"
                                                        value={formData.organisation_name}
                                                        onChange={handleInputChange}
                                                        required
                                                        error={errors.organisation_name}
                                                        helperText={errors.organisation_name ? "Organisation name is required" : ""}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Business />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                                
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" sx={{ mb: 2, mt: 2 }}>
                                                        Organisation Members *
                                                    </Typography>
                                                    
                                                    {organisationMembers.map((member, index) => (
                                                        <Paper 
                                                            key={index} 
                                                            sx={{ p: 2, mb: 2, position: 'relative', bgcolor: 'grey.50' }}
                                                        >
                                                            {organisationMembers.length > 1 && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleRemoveMember(index)}
                                                                    sx={{ position: 'absolute', top: 8, right: 8 }}
                                                                    color="error"
                                                                >
                                                                    <RemoveCircleOutline fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                            
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={12} sm={4}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label={`Member ${index + 1} Name *`}
                                                                        value={member.name}
                                                                        onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                                                                        size="small"
                                                                        required
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={3}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Email"
                                                                        value={member.email}
                                                                        onChange={(e) => handleMemberChange(index, 'email', e.target.value)}
                                                                        size="small"
                                                                        type="email"
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={3}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Phone *"
                                                                        value={member.phone}
                                                                        onChange={(e) => handleMemberChange(index, 'phone', e.target.value)}
                                                                        size="small"
                                                                        required
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={2}>
                                                                    <FormControl fullWidth size="small" required>
                                                                        <Select
                                                                            value={member.room_type}
                                                                            onChange={(e) => handleMemberChange(index, 'room_type', e.target.value)}
                                                                            displayEmpty
                                                                        >
                                                                            <MenuItem value="" disabled>Room</MenuItem>
                                                                            {rooms.map((room) => (
                                                                                <MenuItem key={room.id} value={room.id}>
                                                                                    {room.roomtype}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                </Grid>
                                                            </Grid>
                                                        </Paper>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        Please select a booking type to continue.
                                    </Alert>
                                )}
                            </>
                        )}

                        {activeStep === 2 && (
                            <>
                                <Card sx={{ mb: 3 }}>
                                    <CardHeader 
                                        title="Payment & Billing Details" 
                                        avatar={<Payment color="primary" />}
                                    />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Booking Charge"
                                                    type="number"
                                                    name="booking_charge"
                                                    value={formData.booking_charge}
                                                    onChange={handleInputChange}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AttachMoney />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Discount Amount"
                                                    type="number"
                                                    name="discount_amount"
                                                    value={formData.discount_amount}
                                                    onChange={handleInputChange}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AttachMoney />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth required>
                                                    <InputLabel>Payment Mode *</InputLabel>
                                                    <Select
                                                        name="payment_mode"
                                                        value={formData.payment_mode}
                                                        onChange={handleInputChange}
                                                        label="Payment Mode *"
                                                    >
                                                        <MenuItem value="">
                                                            <em>Select Payment Mode</em>
                                                        </MenuItem>
                                                        {paymentModes.map((mode) => (
                                                            <MenuItem key={mode.id} value={mode.id}>
                                                                {mode.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            
                                            {/* Mobile Money Details */}
                                            {formData.payment_mode === 'mobile' && (
                                                <>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            label="Mobile Money Phone *"
                                                            name="mobile_money_phone"
                                                            value={formData.mobile_money_phone}
                                                            onChange={handleInputChange}
                                                            required
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <Smartphone />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            label="Reference Number *"
                                                            name="mobile_money_ref"
                                                            value={formData.mobile_money_ref}
                                                            onChange={handleInputChange}
                                                            required
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <LocalOffer />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}

                                            {/* Bank Transfer Details */}
                                            {formData.payment_mode === 'bank' && (
                                                <>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth required>
                                                            <InputLabel>Bank Name *</InputLabel>
                                                            <Select
                                                                name="bank_name"
                                                                value={formData.bank_name}
                                                                onChange={handleInputChange}
                                                                label="Bank Name *"
                                                            >
                                                                <MenuItem value="">
                                                                    <em>Select Bank</em>
                                                                </MenuItem>
                                                                {banksData.map((bank) => (
                                                                    <MenuItem key={bank.id} value={bank.id}>
                                                                        {bank.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            label="Card/Account No *"
                                                            name="card_no"
                                                            value={formData.card_no}
                                                            onChange={handleInputChange}
                                                            required
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <CreditCard />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                            
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Advance Amount *"
                                                    type="number"
                                                    name="advance_amount"
                                                    value={formData.advance_amount}
                                                    onChange={handleInputChange}
                                                    required
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AttachMoney />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Advance Remarks"
                                                    name="advance_remarks"
                                                    value={formData.advance_remarks}
                                                    onChange={handleInputChange}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Comment />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>

                                <Card sx={{ mb: 3 }}>
                                    <CardHeader 
                                        title="Summary" 
                                        avatar={<CheckCircle color="primary" />}
                                    />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="body1" fontWeight="bold">Booking Charge:</Typography>
                                            </Grid>
                                            <Grid item xs={6} textAlign="right">
                                                <Typography variant="body1">{formatCurrency(formData.booking_charge)}</Typography>
                                            </Grid>
                                            
                                            <Grid item xs={6}>
                                                <Typography variant="body1" fontWeight="bold">Discount:</Typography>
                                            </Grid>
                                            <Grid item xs={6} textAlign="right">
                                                <Typography variant="body1" color="error">- {formatCurrency(formData.discount_amount)}</Typography>
                                            </Grid>
                                            
                                            <Grid item xs={6}>
                                                <Typography variant="h6" fontWeight="bold">Total Amount:</Typography>
                                            </Grid>
                                            <Grid item xs={6} textAlign="right">
                                                <Typography variant="h6" color="primary">
                                                    {formatCurrency(formData.total_amount)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body1" fontWeight="bold">Advance Paid:</Typography>
                                            </Grid>
                                            <Grid item xs={6} textAlign="right">
                                                <Typography variant="body1" color="success">
                                                    {formatCurrency(formData.advance_amount)}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Typography variant="body1" fontWeight="bold">Balance Due:</Typography>
                                            </Grid>
                                            <Grid item xs={6} textAlign="right">
                                                <Typography variant="body1" color="warning">
                                                    {formatCurrency(formData.total_amount - formData.advance_amount)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </>
                        )}

                        {activeStep === 3 && (
                            <Card>
                                <CardHeader 
                                    title="Confirmation" 
                                    avatar={<CheckCircle color="success" />}
                                />
                                <CardContent>
                                    <Alert severity="success" sx={{ mb: 3 }}>
                                        <Typography variant="h6">Ready to {postStatus === "ADD" ? "create" : "update"} booking!</Typography>
                                        <Typography variant="body2">
                                            Please review all details before submitting.
                                        </Typography>
                                    </Alert>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary">Booking Number:</Typography>
                                            <Typography variant="body1" fontWeight="bold">{formData.booking_no}</Typography>
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Check In:</Typography>
                                            <Typography variant="body1">{formData.check_in ? new Date(formData.check_in).toLocaleDateString() : 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Check Out:</Typography>
                                            <Typography variant="body1">{formData.check_out ? new Date(formData.check_out).toLocaleDateString() : 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Booking Type:</Typography>
                                            <Typography variant="body1">
                                                {formData.booking_type === 1 ? 'Individual' : 'Organisation'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Room:</Typography>
                                            <Typography variant="body1">
                                                {getCategoriesNameById(formData.room_type, rooms)}
                                            </Typography>
                                        </Grid>
                                        
                                        {formData.booking_type === 1 ? (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="subtitle2" color="text.secondary">Customer:</Typography>
                                                    <Typography variant="body1">{formData.customer_name}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="subtitle2" color="text.secondary">Mobile:</Typography>
                                                    <Typography variant="body1">{formData.customer_mobile}</Typography>
                                                </Grid>
                                            </>
                                        ) : formData.booking_type === 2 ? (
                                            <>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" color="text.secondary">Organisation:</Typography>
                                                    <Typography variant="body1" fontWeight="bold">{formData.organisation_name}</Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" color="text.secondary">Members ({organisationMembers.length}):</Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        {organisationMembers.map((member, idx) => (
                                                            <Paper key={idx} sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
                                                                <Typography variant="body2">
                                                                    <strong>{member.name}</strong> - {member.phone} - {getCategoriesNameById(member.room_type, rooms)}
                                                                </Typography>
                                                            </Paper>
                                                        ))}
                                                    </Box>
                                                </Grid>
                                            </>
                                        ) : (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" color="text.secondary">Please select a booking type</Typography>
                                            </Grid>
                                        )}
                                        
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Total Amount:</Typography>
                                            <Typography variant="body1" fontWeight="bold" color="primary">
                                                {formatCurrency(formData.total_amount)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Advance Paid:</Typography>
                                            <Typography variant="body1" color="success">
                                                {formatCurrency(formData.advance_amount)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                </DialogContent>
                
                <DialogActions sx={{
                    px: 3,
                    py: 2,
                    justifyContent: 'space-between'
                }}>
                    <Box>
                        <Button
                            color="error"
                            onClick={handleCloseAdd}
                            variant="outlined"
                            sx={{ mr: 1 }}
                        >
                            Cancel
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            variant="outlined"
                        >
                            Back
                        </Button>
                        
                        {activeStep === steps.length - 1 ? (
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                onClick={handleSubmit}
                            >
                                {postStatus === "ADD" ? "Create Booking" : "Update Booking"}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNext}
                                color="primary"
                                variant="contained"
                            >
                                Next
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={handleCloseView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ 
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Booking Details</Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<Print />}
                        onClick={() => handlePrintBooking(selectedRecord)}
                        size="small"
                    >
                        Print
                    </Button>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Booking Number:</b></Typography>
                                    <Typography variant="body1">{selectedRecord.booking_no || `BK${selectedRecord.id.toString().padStart(5, '0')}`}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Status:</b></Typography>
                                    {selectedRecord.status === 1 ? (
                                        <Chip
                                            label={getStatusById(selectedRecord.status)}
                                            size="small"
                                            color="primary"
                                            sx={{ mt: 0.5 }}
                                        />
                                    ) : selectedRecord.status === 2 ? (
                                        <Chip
                                            label="Checked In"
                                            size="small"
                                            color="success"
                                            sx={{ mt: 0.5 }}
                                        />
                                    ) : selectedRecord.status === 3 ? (
                                        <Chip
                                            label="Checked Out"
                                            size="small"
                                            color="info"
                                            sx={{ mt: 0.5 }}
                                        />
                                    ) : (
                                        <Chip
                                            label={getStatusById(selectedRecord.status)}
                                            size="small"
                                            color="error"
                                            sx={{ mt: 0.5 }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Booking Type:</b></Typography>
                                    <Typography variant="body1">
                                        {selectedRecord.booking_type === 1 ? 'Individual' : 'Organisation'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Check In:</b></Typography>
                                    <Typography variant="body1">{new Date(selectedRecord.check_in).toLocaleDateString()}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Check Out:</b></Typography>
                                    <Typography variant="body1">{new Date(selectedRecord.check_out).toLocaleDateString()}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Room Type:</b></Typography>
                                    <Typography variant="body1">{getCategoriesNameById(selectedRecord.room_type, rooms)}</Typography>
                                </Grid>

                                {/* New Columns Display */}
                                {selectedRecord.final_amount > 0 && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2"><b>Final Amount:</b></Typography>
                                        <Typography variant="body1" color="primary">
                                            {formatCurrency(selectedRecord.final_amount)}
                                        </Typography>
                                    </Grid>
                                )}
                                {selectedRecord.balance_paid > 0 && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2"><b>Balance Paid:</b></Typography>
                                        <Typography variant="body1" color="success">
                                            {formatCurrency(selectedRecord.balance_paid)}
                                        </Typography>
                                    </Grid>
                                )}
                                {selectedRecord.additional_charges && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2"><b>Additional Charges:</b></Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                                            {(() => {
                                                try {
                                                    const charges = typeof selectedRecord.additional_charges === 'string' 
                                                        ? JSON.parse(selectedRecord.additional_charges) 
                                                        : selectedRecord.additional_charges;
                                                    return (
                                                        <Grid container spacing={1}>
                                                            {charges.damage_charges > 0 && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">Damage Charges: {formatCurrency(charges.damage_charges)}</Typography>
                                                                </Grid>
                                                            )}
                                                            {charges.late_checkout_charges > 0 && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">Late Checkout Charges: {formatCurrency(charges.late_checkout_charges)}</Typography>
                                                                </Grid>
                                                            )}
                                                            {charges.other_charges > 0 && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">Other Charges: {formatCurrency(charges.other_charges)}</Typography>
                                                                </Grid>
                                                            )}
                                                            {charges.orders_total > 0 && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">Restaurant Orders: {formatCurrency(charges.orders_total)}</Typography>
                                                                </Grid>
                                                            )}
                                                            {charges.bar_orders_total > 0 && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">Bar Orders: {formatCurrency(charges.bar_orders_total)}</Typography>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    );
                                                } catch (e) {
                                                    return <Typography variant="body2">{selectedRecord.additional_charges}</Typography>;
                                                }
                                            })()}
                                        </Paper>
                                    </Grid>
                                )}
                                {selectedRecord.checkout_remarks && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2"><b>Checkout Remarks:</b></Typography>
                                        <Typography variant="body1">{selectedRecord.checkout_remarks}</Typography>
                                    </Grid>
                                )}
                                
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Payment Mode:</b></Typography>
                                    <Typography variant="body1">
                                        {paymentModes.find(mode => mode.id === selectedRecord.payment_mode)?.name || selectedRecord.payment_mode || 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Total Amount:</b></Typography>
                                    <Typography variant="body1" fontWeight="bold" color="primary">
                                        {formatCurrency(selectedRecord.total_amount || 0)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Advance Paid:</b></Typography>
                                    <Typography variant="body1" color="success">
                                        {formatCurrency(selectedRecord.advance_amount || 0)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Balance Due:</b></Typography>
                                    <Typography variant="body1" color="warning">
                                        {formatCurrency((selectedRecord.total_amount || 0) - (selectedRecord.advance_amount || 0))}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Remarks:</b></Typography>
                                    <Typography variant="body1">{selectedRecord.booking_remarks || 'No remarks'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Created Date:</b></Typography>
                                    <Typography variant="body1">{formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Last Updated:</b></Typography>
                                    <Typography variant="body1">{formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                                </Grid>
                            </Grid>
                                <br/><br/>
                                {selectedRecord.booking_type === 1 ? (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Customer Name:</b> {selectedRecord.customer_name || 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Mobile:</b> {selectedRecord.customer_mobile || 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Email:</b> {selectedRecord.customer_email || 'N/A'}</Typography>
                                        </Grid>
                                    </>
                                ) : (
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2"><b>Organisation Name:</b> {selectedRecord.organisation_name || 'N/A'}</Typography>
                                        </Grid>
                                        <Box>
                                            <Typography variant="subtitle2"><b>Members:</b></Typography>
                                            {selectedRecord.organisation_members && selectedRecord.organisation_members.length > 0 ? (
                                                <Box sx={{ mt: 1 }}>
                                                    <TableContainer>
                                                        <Table>
                                                            <TableHead>
                                                                <TableRow sx={{
                                                                backgroundColor: theme.palette.primary.main,
                                                                '& th': {
                                                                    color: 'white',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '0.95rem'
                                                                }
                                                                }}>
                                                                <TableCell>Name</TableCell>
                                                                <TableCell>Phone</TableCell>
                                                                <TableCell>Email</TableCell>
                                                                <TableCell>Room</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {selectedRecord.organisation_members.map((member, idx) => (
                                                                    <TableRow key={idx}>
                                                                            <TableCell>{member.name}</TableCell>
                                                                            <TableCell>{member.phone}</TableCell>
                                                                            <TableCell>{member.email} </TableCell>
                                                                            <TableCell>{getCategoriesNameById(member.room_type, rooms)}</TableCell>
                                                                    </TableRow>
                                                            ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2">No members listed</Typography>
                                            )}
                                        </Box>
                                    </>
                                )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseView}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={handleCloseDelete}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Booking</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this booking?
                    </Typography>
                    {selectedRecord && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Booking #{selectedRecord.booking_no || `BK${selectedRecord.id.toString().padStart(5, '0')}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedRecord.booking_type === 1 
                                    ? `Customer: ${selectedRecord.customer_name || 'N/A'}`
                                    : `Organisation: ${selectedRecord.organisation_name || 'N/A'}`
                                }
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Room: {getCategoriesNameById(selectedRecord.room_type, rooms)}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseDelete}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                    >
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Print Booking Dialog */}
            <Dialog
                open={isPrintDialogOpen}
                onClose={handleClosePrintDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ textAlign: 'center', bgcolor: theme.palette.primary.main, color: 'white' }}>
                    <b>Print Booking - {printBookingData?.booking_no}</b>
                </DialogTitle>
                <DialogContent>
                    {printBookingData && (
                        <PrintBooking
                            bookingData={printBookingData}
                            rooms={rooms}
                            onPrintComplete={handleClosePrintDialog}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleClosePrintDialog}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => {
                            window.print();
                        }}
                        color="primary"
                        variant="contained"
                        startIcon={<Print />}
                    >
                        Print Now
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Booking;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});